import { useEffect, useRef, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Alert,
  Space,
  message,
  Spin,
  Popconfirm,
} from 'antd';
import type { ToolkitResponse, ToolkitType } from '@viq-admin/types';
import { MaskedSecretField, errorMessageWithFieldErrors, space } from '@viq-admin/ui';
import { getToolkit, putToolkit, deleteToolkit } from '../../api/conferences';
import { Can } from '../../ability/AbilityContext';
import { ATS_LAYOUT_EXTRA, ATS_LAYOUT_PLACEHOLDER, ATS_PASSWORD_EXTRA, ATS_PASSWORD_WHITESPACE_RULE } from '../../resources/toolkits/atsCopy';
import { FPLEADS_BASE_URL_EXTRA, FPLEADS_DEFAULT_BASE_URL, FPLEADS_HTTPS_RULE, FPLEADS_SECRETS_EXTRA, FPLEADS_WHITESPACE_RULE } from '../../resources/toolkits/fpleadsCopy';
import { ATS_API_PASSWORD_RULE, ATS_API_SECRETS_EXTRA, ATS_API_SHOW_ID_EXTRA, ATS_API_SHOW_ID_RULE, ATS_API_USERNAME_RULE } from '../../resources/toolkits/atsApiCopy';
import { SHOWCARE_BASE_URL_EXTRA, SHOWCARE_DEFAULT_BASE_URL, SHOWCARE_HTTPS_RULE, SHOWCARE_LICENSE_KEY_EXTRA, SHOWCARE_LICENSE_KEY_RULE, SHOWCARE_BASE_URL_SHAPE_RULE } from '../../resources/toolkits/showcareCopy';
import { switchBaseUrl, type BaseUrlMemory } from '../../resources/toolkits/baseUrlDefaults';

/**
 * Per-conference Toolkit config tab. Direct port of the customer SPA's toolkit
 * editor (`src/views/ConferenceDetail/Toolkit.tsx`) with the API swapped to the
 * super-admin wrappers (`getToolkit`/`putToolkit`/`deleteToolkit`). The form is
 * type-discriminated: changing `toolkitType` swaps the visible fields.
 *
 *  - Secrets are write-only via `MaskedSecretField` (`hasSecret` is the only
 *    readable signal; the value is never echoed back).
 *  - Submit omits the `secrets` object entirely when every secret field is left
 *    blank (preserving the stored secret).
 *  - `putToolkit` returns void, so we re-`refresh()` after save to pick up the
 *    new `hasSecret` / `updatedAt`.
 *
 * Writes are gated on `manage Conference`.
 */

const DEFAULT_TOOLKIT_TYPE: ToolkitType = 'attendee_list';

const TOOLKIT_OPTIONS: { label: string; value: ToolkitType }[] = [
  { label: 'attendee_list', value: 'attendee_list' },
  { label: 'plus_lead', value: 'plus_lead' },
  { label: 'expo_tools', value: 'expo_tools' },
  { label: 'badge_kit', value: 'badge_kit' },
  { label: 'conexsys (not implemented)', value: 'conexsys' },
  { label: 'xpress_lead_pro', value: 'xpress_lead_pro' },
  { label: 'maritz_swap', value: 'maritz_swap' },
  { label: 'edc', value: 'edc' },
  { label: 'exposmart', value: 'exposmart' },
  { label: 'ats', value: 'ats' },
  { label: 'fpleads', value: 'fpleads' },
  { label: 'showcare', value: 'showcare' },
  { label: 'ats_api', value: 'ats_api' },
];

const TOOLKITS_WITH_SECRETS: Record<ToolkitType, boolean> = {
  attendee_list: true,
  plus_lead: false,
  expo_tools: false,
  badge_kit: true,
  conexsys: true,
  xpress_lead_pro: false,
  maritz_swap: false,
  edc: false,
  exposmart: true,
  ats: true,
  fpleads: true,
  showcare: true,
  ats_api: true,
};

interface FormShape {
  toolkitType: ToolkitType;
  config: Record<string, string>;
  secrets: Record<string, string>;
}

export function ConferenceToolkit({ conferenceId }: { conferenceId: string }) {
  const [form] = Form.useForm<FormShape>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toolkit, setToolkit] = useState<ToolkitResponse | null>(null);
  const [toolkitType, setToolkitType] = useState<ToolkitType>(DEFAULT_TOOLKIT_TYPE);

  const [loadError, setLoadError] = useState(false);
  const formShownRef = useRef(false);
  const baseUrlMemory = useRef<BaseUrlMemory>({});
  useEffect(() => {
    if (!loading && !loadError) formShownRef.current = true;
  }, [loading, loadError]);

  // Back to the create state. resetFields restores the select to the default
  // type, so keep the rendered fields in step with it. Before the form first
  // renders its store is already pristine; resetting then only trips
  // rc-field-form's "not connected to any Form" warning.
  function showEmptyForm() {
    if (formShownRef.current) form.resetFields();
    setToolkitType(DEFAULT_TOOLKIT_TYPE);
    setToolkit(null);
  }

  async function refresh() {
    // One conference's remembered URLs must never leak into another's form.
    baseUrlMemory.current = {};
    setLoading(true);
    try {
      const data = await getToolkit(conferenceId);
      setLoadError(false);
      if (data) {
        setToolkit(data);
        setToolkitType(data.toolkitType);
        form.setFieldValue('toolkitType', data.toolkitType);
        // Replace, not deep-merge: another conference's config keys must not linger.
        form.setFieldValue('config', data.config as Record<string, string>);
        // setFieldsValue deep-merges, so an empty object would clear nothing.
        form.setFieldValue('secrets', {});
      } else {
        // No toolkit configured: start create from a clean form (also clears
        // a previous conference's values on a same-instance switch).
        showEmptyForm();
      }
    } catch (err) {
      // Never keep showing the previous conference's (or a deleted) toolkit.
      setToolkit(null);
      setLoadError(true);
      message.error(
        (err as { message?: string }).message ?? 'Failed to load toolkit',
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conferenceId]);

  async function onFinish(values: FormShape) {
    setSaving(true);
    try {
      const secretsFilled = Object.values(values.secrets ?? {}).filter(
        (v) => typeof v === 'string' && v.length > 0,
      ).length;
      const body: {
        toolkitType: ToolkitType;
        config: Record<string, unknown>;
        secrets?: Record<string, string>;
      } = {
        toolkitType: values.toolkitType,
        config: values.config ?? {},
      };
      if (secretsFilled > 0) {
        body.secrets = values.secrets;
      }
      await putToolkit(conferenceId, body);
      // Clear the secret fields so the next render shows the
      // "Leave blank to keep existing" placeholder, then re-read to refresh
      // hasSecret / updatedAt.
      form.setFieldValue('secrets', {});
      await refresh();
      message.success(
        'Saved. Live scans use the new settings within 1 hour (restart the customer Function App to apply now). Reps see a newly added toolkit after reopening the app.',
      );
    } catch (err) {
      // A 422 carries the reason (e.g. an ATS layout with no last name) in its
      // field errors; the bare message is just "Validation failed".
      message.error(errorMessageWithFieldErrors(err, 'Failed to save toolkit'), 8);
    } finally {
      setSaving(false);
    }
  }

  async function onDelete() {
    try {
      await deleteToolkit(conferenceId);
      message.success('Toolkit removed');
      // Even if the reload below fails, the deleted toolkit must not stay editable.
      showEmptyForm();
      await refresh();
    } catch (err) {
      message.error(
        (err as { message?: string }).message ?? 'Failed to delete toolkit',
      );
    }
  }

  if (loading) return <Spin />;

  if (loadError) {
    return (
      <Alert
        type="error"
        showIcon
        message="Failed to load the toolkit configuration."
        action={
          <Button size="small" onClick={() => void refresh()}>
            Retry
          </Button>
        }
      />
    );
  }

  const hasSecret = toolkit?.hasSecret ?? false;
  // A stored secret only counts for the type it was saved with: after switching
  // e.g. exposmart → ats, the stored secret is the old vendor's api_key.
  const secretStoredForType = hasSecret && toolkit?.toolkitType === toolkitType;
  const showsSecretFields = TOOLKITS_WITH_SECRETS[toolkitType];

  return (
    <Card
      title={toolkit ? 'Edit toolkit' : 'Configure toolkit'}
      extra={
        toolkit && (
          <Can I="manage" a="Conference" passThrough>
            {(allowed: boolean) =>
              allowed ? (
                <Popconfirm
                  title="Remove toolkit configuration?"
                  onConfirm={onDelete}
                >
                  <Button danger>Delete</Button>
                </Popconfirm>
              ) : null
            }
          </Can>
        )
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ toolkitType: DEFAULT_TOOLKIT_TYPE, config: {}, secrets: {} }}
      >
        {toolkit?.sourceConfigId && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: space.lg }}
            message={
              <span>
                Applied from config <strong>{toolkit.sourceConfigName}</strong>.
                Edit that reusable config and re-apply to update every
                conference. Saving here detaches this conference from the
                config.
              </span>
            }
          />
        )}

        <Form.Item
          label="Toolkit type"
          name="toolkitType"
          rules={[{ required: true }]}
        >
          <Select
            options={TOOLKIT_OPTIONS}
            onChange={(v) => {
              const type = v as ToolkitType;
              const next = switchBaseUrl(baseUrlMemory.current, toolkitType, type, form.getFieldValue(['config', 'base_url']));
              if (next) form.setFieldValue(['config', 'base_url'], next);
              // Two types can share a secret field name (ats/ats_api `password`, exposmart/fpleads `api_key`), and antd keeps the store value, so a typed secret would reappear under the new vendor.
              form.setFieldValue('secrets', {});
              setToolkitType(type);
            }}
          />
        </Form.Item>

        {toolkitType === 'attendee_list' && (
          <>
            <Form.Item
              label="API URL"
              name={['config', 'url']}
              rules={[{ required: true, type: 'url' }]}
            >
              <Input placeholder="https://api.example.com/attendee" />
            </Form.Item>
            <Form.Item
              label="Event UID"
              name={['config', 'evt_uid']}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Token"
              name={['secrets', 'token']}
              rules={
                !secretStoredForType ? [{ required: true, message: 'Token required' }] : []
              }
            >
              <MaskedSecretField hasSecret={secretStoredForType} />
            </Form.Item>
          </>
        )}

        {toolkitType === 'plus_lead' && (
          <Form.Item
            label="Filled URL"
            name={['config', 'filled_url']}
            rules={[{ required: true, type: 'url' }]}
            help="Include (barcode) as a placeholder, e.g. https://api/x?id=(barcode)"
          >
            <Input />
          </Form.Item>
        )}

        {toolkitType === 'expo_tools' && (
          <Form.Item
            label="Filled URL"
            name={['config', 'filled_url']}
            rules={[{ required: true, type: 'url' }]}
            help="Include (scanCode) as a placeholder"
          >
            <Input />
          </Form.Item>
        )}

        {toolkitType === 'xpress_lead_pro' && (
          <Form.Item
            label="Filled URL"
            name={['config', 'filled_url']}
            rules={[{ required: true, type: 'url' }]}
            help={
              'Paste the full XpressLeadPro filled_url including authkey, ' +
              'exid, eventcode, and placeholder tokens. Required tokens: ' +
              '(badgeNumber), (lastName), (utc_time), (current_time). ' +
              'Also supported: (firstName), (company), (showCode), (barcode).'
            }
          >
            <Input />
          </Form.Item>
        )}

        {toolkitType === 'maritz_swap' && (
          <Form.Item
            label="Filled URL"
            name={['config', 'filled_url']}
            rules={[{ required: true, type: 'url' }]}
            help={
              'Maritz SWAP LeadInfo URL with apikey, actcode, and ' +
              'operator badgeid baked in, plus (barcode) as a ' +
              'placeholder, e.g. https://developer.swap.mge360.com/' +
              'APIv1/LeadInfo?apikey=X&actcode=Y&badgeid=Z&barcode=(barcode)'
            }
          >
            <Input />
          </Form.Item>
        )}

        {toolkitType === 'edc' && (
          <Form.Item
            label="Filled URL"
            name={['config', 'filled_url']}
            rules={[{ required: true, type: 'url' }]}
            help={
              'EDC (retrievemyleads.com) LeadInfo URL with sid (show ' +
              'code) and key baked in, plus (aid) as a placeholder, ' +
              'e.g. https://retrievemyleads.com/api/meta/1.1/read/' +
              '?sid=SHOWCODE&key=KEY&aid=(aid)'
            }
          >
            <Input />
          </Form.Item>
        )}

        {toolkitType === 'badge_kit' && (
          <>
            <Form.Item
              label="Login URL"
              name={['config', 'login_url']}
              rules={[{ required: true, type: 'url' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Data extract URL"
              name={['config', 'data_extract_url']}
              rules={[{ required: true, type: 'url' }]}
              help="Include (referenceId), (eventId), (exhibitorId) placeholders"
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Event ID"
              name={['config', 'eventId']}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Exhibitor ID"
              name={['config', 'exhibitorId']}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="API key"
              name={['secrets', 'apikey']}
              rules={
                !secretStoredForType
                  ? [{ required: true, message: 'API key required' }]
                  : []
              }
            >
              <MaskedSecretField hasSecret={secretStoredForType} />
            </Form.Item>
            <Form.Item
              label="Secret"
              name={['secrets', 'secret']}
              rules={
                !secretStoredForType ? [{ required: true, message: 'Secret required' }] : []
              }
            >
              <MaskedSecretField hasSecret={secretStoredForType} />
            </Form.Item>
          </>
        )}

        {toolkitType === 'exposmart' && (
          <>
            <Form.Item
              label="URL"
              name={['config', 'url']}
              rules={[{ required: true, type: 'url' }]}
            >
              <Input placeholder="https://api.rcsreg.com/w/exposmart/at/v1.json" />
            </Form.Item>
            <Form.Item
              label="API Key"
              name={['secrets', 'api_key']}
              rules={
                !secretStoredForType
                  ? [{ required: true, message: 'API Key required' }]
                  : []
              }
            >
              <MaskedSecretField hasSecret={secretStoredForType} />
            </Form.Item>
          </>
        )}

        {toolkitType === 'ats' && (
          <>
            <Form.Item
              label="Field layout"
              name={['config', 'field_layout']}
              rules={[{ required: true, message: 'Field layout required' }]}
              extra={ATS_LAYOUT_EXTRA}
            >
              <Input placeholder={ATS_LAYOUT_PLACEHOLDER} />
            </Form.Item>
            <Form.Item
              label="Password"
              name={['secrets', 'password']}
              extra={ATS_PASSWORD_EXTRA}
              rules={[
                ...(!secretStoredForType
                  ? [{ required: true, message: 'Password required' }]
                  : []),
                ATS_PASSWORD_WHITESPACE_RULE,
              ]}
            >
              <MaskedSecretField hasSecret={secretStoredForType} />
            </Form.Item>
          </>
        )}

        {toolkitType === 'fpleads' && (
          <>
            <Form.Item
              label="Base URL"
              name={['config', 'base_url']}
              initialValue={FPLEADS_DEFAULT_BASE_URL}
              validateFirst
              rules={[{ required: true, type: 'url' }, FPLEADS_HTTPS_RULE]}
              extra={FPLEADS_BASE_URL_EXTRA}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="API Key"
              name={['secrets', 'api_key']}
              extra={FPLEADS_SECRETS_EXTRA}
              dependencies={[['secrets', 'user_id']]}
              rules={[
                ({ getFieldValue }) => ({
                  required: !secretStoredForType || Boolean(getFieldValue(['secrets', 'user_id'])),
                  message: 'API Key required',
                }),
                FPLEADS_WHITESPACE_RULE,
              ]}
            >
              <MaskedSecretField hasSecret={secretStoredForType} />
            </Form.Item>
            <Form.Item
              label="User ID"
              name={['secrets', 'user_id']}
              dependencies={[['secrets', 'api_key']]}
              rules={[
                ({ getFieldValue }) => ({
                  required: !secretStoredForType || Boolean(getFieldValue(['secrets', 'api_key'])),
                  message: 'User ID required',
                }),
                FPLEADS_WHITESPACE_RULE,
              ]}
            >
              <MaskedSecretField hasSecret={secretStoredForType} />
            </Form.Item>
          </>
        )}

        {toolkitType === 'showcare' && (
          <>
            <Form.Item
              label="Base URL"
              name={['config', 'base_url']}
              initialValue={SHOWCARE_DEFAULT_BASE_URL}
              validateFirst
              rules={[{ required: true, type: 'url' }, SHOWCARE_HTTPS_RULE, SHOWCARE_BASE_URL_SHAPE_RULE]}
              extra={SHOWCARE_BASE_URL_EXTRA}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="License key"
              name={['secrets', 'license_key']}
              extra={SHOWCARE_LICENSE_KEY_EXTRA}
              rules={[
                ...(!secretStoredForType ? [{ required: true, message: 'License key required' }] : []),
                SHOWCARE_LICENSE_KEY_RULE,
              ]}
            >
              <MaskedSecretField hasSecret={secretStoredForType} />
            </Form.Item>
          </>
        )}

        {toolkitType === 'ats_api' && (
          <>
            <Form.Item
              label="Show ID"
              name={['config', 'show_id']}
              rules={[{ required: true, message: 'Show ID required' }, ATS_API_SHOW_ID_RULE]}
              extra={ATS_API_SHOW_ID_EXTRA}
            >
              <Input inputMode="numeric" />
            </Form.Item>
            <Form.Item
              label="Username"
              name={['secrets', 'username']}
              extra={ATS_API_SECRETS_EXTRA}
              dependencies={[['secrets', 'password']]}
              rules={[
                ({ getFieldValue }) => ({
                  required: !secretStoredForType || Boolean(getFieldValue(['secrets', 'password'])),
                  message: 'Username required',
                }),
                ATS_API_USERNAME_RULE,
              ]}
            >
              <MaskedSecretField hasSecret={secretStoredForType} />
            </Form.Item>
            <Form.Item
              label="Password"
              name={['secrets', 'password']}
              dependencies={[['secrets', 'username']]}
              rules={[
                ({ getFieldValue }) => ({
                  required: !secretStoredForType || Boolean(getFieldValue(['secrets', 'username'])),
                  message: 'Password required',
                }),
                ATS_API_PASSWORD_RULE,
              ]}
            >
              <MaskedSecretField hasSecret={secretStoredForType} />
            </Form.Item>
          </>
        )}

        {toolkitType === 'conexsys' && (
          <Alert
            type="warning"
            showIcon
            message="Conexsys integration not implemented yet — saving config is allowed, but the badge-scan endpoint returns 501 until the backend handler ships."
            style={{ marginBottom: space.lg }}
          />
        )}

        {showsSecretFields && secretStoredForType && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: space.lg }}
            message="A secret is already stored. Leave secret fields blank to keep it. Type a new value to overwrite."
          />
        )}

        <Can I="manage" a="Conference" passThrough>
          {(allowed: boolean) =>
            allowed ? (
              <Space>
                <Button type="primary" htmlType="submit" loading={saving}>
                  Save
                </Button>
              </Space>
            ) : (
              <Alert
                type="info"
                showIcon
                message="Read-only: your role cannot modify toolkit configuration."
              />
            )
          }
        </Can>
      </Form>
    </Card>
  );
}
